import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useTitle } from "@/lib/brand";
import { PageHead } from "@/components/Kit";

/**
 * HOW IT WORKS — Dog's seven steps, 2026-10-27 (20:26), in his words, lightly tidied. "Go offline"
 * moved to 4. The site says "Edition" where Dog says "Gen" — the app's 10-24 naming.
 */
const STEPS = [
  ["Install the app", "Free, with no account to create. Experts never asks for your name, email or phone number."],
  ["Choose your Engine", "Your phone picks the Engine it can run — the 4B or the 7B. When the download finishes, you have a general AI on your phone that's yours forever."],
  ["Add an Expert", "Start with a free Expert, or train your own. An Expert speaks up when it holds better, more up-to-date knowledge than the Engine does."],
  ["Go offline", "Turn on airplane mode. Experts keeps working — the thinking happens in the Engine, on your phone's own hardware. No cloud, no subscription, and no personal information ever leaves your device."],
  ["Ask the way you'd ask a person", "The Engine is a general-knowledge AI. When an Expert answers, tap Sources to read the exact notes it used and where that knowledge was found."],
  ["Train it again", "Every new Edition is trained on everything the last one knew, plus newly found knowledge. An Edition 16 Expert has been trained on the same subject sixteen times. Knowledge is never duplicated — only compounded."],
  ["Keep it forever", "Free Experts and the Experts you train are yours forever. No subscription. Train it — own it."],
];

export default function HowItWorksPage() {
  useTitle("How it works");
  return (
    <main data-testid="how-it-works-page" className="ex-page">
      <div className="ex-wrap pb-28">
        <PageHead eyebrow="How it works" title="From download to your own Expert." lede="Experts has two parts: the Engine, the AI that does the thinking, and Experts — specialists that sit on top of it, each with a library of checked facts." />
        <ol className="grid gap-4 md:grid-cols-2">
          {STEPS.map(([h, b], i) => (
            <li key={h} className={`ex-card p-7 ${i === STEPS.length - 1 ? "md:col-span-2" : ""}`}>
              <div className="ex-num text-[40px] text-white leading-none">{String(i + 1).padStart(2, "0")}</div>
              <h2 className="ex-h3 mt-6">{h}</h2>
              <p className="ex-body mt-2">{b}</p>
            </li>
          ))}
        </ol>

        <div className="ex-panel p-8 md:p-10 mt-16 grid gap-8 md:grid-cols-2">
          <div>
            <div className="ex-eyebrow">What leaves your phone</div>
            <p className="ex-body mt-3">Nothing you type in a conversation. The app goes online only to download the Engine and the Experts you choose, to follow a training you started, and — only if you tap Report — to send the answer you reported, without your question.</p>
          </div>
          <div>
            <div className="ex-eyebrow">What training costs</div>
            <p className="ex-body mt-3">Training runs on our hardware, because a phone can't do it. You pay once per Expert. Every tier is checked the same careful way; the higher tiers read more and test longer.</p>
            <Link to="/train" className="ex-btn ex-btn-quiet ex-btn-sm -ml-4 mt-4">See prices <ArrowRight className="h-4 w-4" strokeWidth={1.8} /></Link>
          </div>
        </div>
      </div>
    </main>
  );
}
